import { CheckIn, GetPointResponse, GetTopicResponse, TopicCategory } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { getMarkerIcon, preloadCustomIcons } from '../helpers/markers';

import { GOOGLE_KEY } from '../helpers/api/google-api';
import { MAP_STYLES_COLLECTION } from '../helpers/constants';
import { StateController } from '../helpers/state/state-management/state-controller';
import { isDefined } from '../helpers/utils';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

/**
 * Layout options for the static map component
 */
export enum StaticMapLayout {
    /** Map on the left, point info on the right (horizontal layout) */
    MapLeftInfoRight = 'map-left-info-right',
    /** Map on the right, point info on the left (horizontal layout) */
    MapRightInfoLeft = 'map-right-info-left',
    /** Map on top, point info below (vertical layout) */
    MapTopInfoBottom = 'map-top-info-bottom',
    /** Map on bottom, point info above (vertical layout) */
    MapBottomInfoTop = 'map-bottom-info-top',
    /** Only show the map, no point info */
    MapOnly = 'map-only',
    /** Only show the point info card, no map */
    CardOnly = 'card-only',
}

/**
 * Static Map Component
 *
 * A component that uses Google's Static Maps API to display a map image
 * instead of the dynamic Google Maps JavaScript API. This is useful for
 * embeds and situations where a lightweight, non-interactive map is preferred.
 *
 * Features:
 * - Uses Google Static Maps API for fast, lightweight loading
 * - Supports styling similar to the dynamic map-component
 * - Can display a single point or multiple points with markers
 * - Point information can be displayed adjacent to the map in various layouts
 */
@customElement('static-map-component')
export class StaticMapComponent extends LitElement {
    static amplitude_callsite: string = 'Static Map Component';

    /**
     * Optional single point ID to load and display on the map.
     * If provided, only this point will be shown.
     */
    @property({ type: String })
    pointId?: string;

    /**
     * Optional array of point IDs to load and display on the map.
     * If provided along with pointId, pointIds takes precedence.
     */
    @property({ type: Array })
    pointIds?: string[];

    /**
     * Optional points to display directly (bypasses pointId/pointIds lookup)
     */
    @property({ type: Array })
    points?: GetPointResponse[];

    /**
     * The layout of the component - where the point info appears relative to the map
     */
    @property({ type: String })
    layout: StaticMapLayout = StaticMapLayout.MapLeftInfoRight;

    /**
     * Width of the static map image in pixels
     */
    @property({ type: Number })
    mapWidth: number = 600;

    /**
     * Height of the static map image in pixels
     */
    @property({ type: Number })
    mapHeight: number = 400;

    /**
     * Zoom level for the map (1-20)
     */
    @property({ type: Number })
    zoom?: number;

    /**
     * Center latitude for the map (optional - will auto-center on points if not provided)
     */
    @property({ type: Number })
    centerLat?: number;

    /**
     * Center longitude for the map (optional - will auto-center on points if not provided)
     */
    @property({ type: Number })
    centerLng?: number;

    /**
     * Whether to show all points from the topic
     */
    @property({ type: Boolean })
    showAllPoints: boolean = false;

    /**
     * Maximum number of points to display markers for (Google Static Maps API has limits)
     */
    @property({ type: Number })
    maxMarkers: number = 50;

    /**
     * Custom map style name (matching the topic's custom_map_style_name)
     */
    @property({ type: String })
    mapStyleName?: string;

    @state()
    private displayPoints: GetPointResponse[] = [];

    @state()
    private isLoading: boolean = true;

    @state()
    private selectedPointIndex: number = 0;

    @state()
    private topic?: GetTopicResponse;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    async connectedCallback() {
        super.connectedCallback();
        await this.loadPoints();
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('pointId') || changedProperties.has('pointIds') || changedProperties.has('points') || changedProperties.has('showAllPoints')) {
            await this.loadPoints();
        }
    }

    /**
     * Load points based on the provided configuration
     */
    private async loadPoints() {
        this.isLoading = true;
        this.topic = topicData.topic;

        // Wait for point cache to be ready with a timeout
        const maxWaitTime = 10000; // 10 seconds
        const startTime = Date.now();
        while (!pointCache.isReady && (Date.now() - startTime) < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (this.points && this.points.length > 0) {
            // Use directly provided points
            this.displayPoints = this.points;
        } else if (this.pointIds && this.pointIds.length > 0) {
            // Load specific points by IDs
            this.displayPoints = this.pointIds
                .map(id => pointCache.getPoint(id))
                .filter((p): p is GetPointResponse => p !== undefined);
        } else if (this.pointId) {
            // Load single point by ID
            const point = pointCache.getPoint(this.pointId);
            this.displayPoints = point ? [point] : [];
        } else if (this.showAllPoints) {
            // Show all points (limited by maxMarkers)
            this.displayPoints = pointCache.allPoints.slice(0, this.maxMarkers);
        } else {
            this.displayPoints = [];
        }

        // Preload all custom icons in parallel first
        await preloadCustomIcons(this.displayPoints, topicData.categories);

        this.selectedPointIndex = 0;
        this.isLoading = false;
    }

    /**
     * Get a marker label for a given index.
     * Uses A-Z for first 26 points, then 1-9 for additional points,
     * and then no label for points beyond 35 (Google Static Maps API limit).
     */
    private getMarkerLabel(index: number): string {
        if (index < 26) {
            return String.fromCharCode(65 + index); // A-Z
        } else if (index < 35) {
            return String(index - 25); // 1-9
        }
        return ''; // No label for points beyond 35
    }

    /**
     * Convert a color to the Static Maps API format (0xRRGGBB)
     */
    private toStaticMapColor(color: string | undefined, defaultColor: string = '0x4285F4'): string {
        if (!color) {
            return defaultColor;
        }
        // If already in 0x format, return as is
        if (color.startsWith('0x')) {
            return color;
        }
        // If in # format, convert
        if (color.startsWith('#')) {
            return `0x${color.substring(1)}`;
        }
        // If no prefix, assume it's a hex color without prefix
        if (/^[0-9A-Fa-f]{6}$/.test(color)) {
            return `0x${color}`;
        }
        // Default fallback
        return defaultColor;
    }

    /**
     * Generate the Google Static Maps API URL
     */
    private getStaticMapUrl(): string {
        const params = new URLSearchParams();

        // Size - use the user-specified dimensions
        params.set('size', `${this.mapWidth}x${this.mapHeight}`);

        // API Key
        params.set('key', GOOGLE_KEY);

        // Scale for retina displays
        params.set('scale', '2');

        // Map type based on style
        const styleName = this.mapStyleName ?? this.topic?.custom_map_style_name;

        // Look up map_id from MAP_STYLES_COLLECTION
        const mapStyle = MAP_STYLES_COLLECTION.find(style => style.title === styleName) ?? MAP_STYLES_COLLECTION[0];
        if (mapStyle?.mapId) {
            params.set('map_id', mapStyle.mapId);
        }

        // Add custom styling if available (only for roadmap when no map_id)
        if (styleName && styleName !== 'satellite' && styleName !== 'hybrid' && styleName !== 'custom' && isDefined(mapStyle?.mapId)) {
            // Apply predefined style colors for Static Maps API
            const styleParams = this.getStyleParams(styleName);
            styleParams.forEach(style => {
                params.append('style', style);
            });
        }

        // Center and zoom
        if (this.displayPoints.length > 0) {
            if (isDefined(this.centerLat) && isDefined(this.centerLng)) {
                params.set('center', `${this.centerLat},${this.centerLng}`);
            } else if (this.displayPoints.length === 1) {
                const point = this.displayPoints[0];
                // Offset center based on layout to keep point visible
                let centerLat = point.location.latLng.lat;
                let centerLng = point.location.latLng.lng;

                // Adjust center to account for overlay card position
                this.zoom = isDefined(this.zoom) ? this.zoom : (topicData.topic?.default_map_zoom ?? 12) + 2;

                // Adjust offset based on zoom and map width/height
                const aspectRatio = this.mapWidth / this.mapHeight;
                const offsetFactor = Math.pow(2, 16 - this.zoom) * 0.00005 * (aspectRatio > 1 ? aspectRatio : 1);


                if (this.layout === StaticMapLayout.MapLeftInfoRight) {
                    // Card on right, shift map center right so point appears left
                    centerLng += offsetFactor * 15;
                } else if (this.layout === StaticMapLayout.MapRightInfoLeft) {
                    // Card on left, shift map center left so point appears right
                    centerLng -= offsetFactor * 15;
                } else if (this.layout === StaticMapLayout.MapTopInfoBottom) {
                    // Card on bottom, shift map center down so point appears up
                    centerLat += offsetFactor * 8;
                } else if (this.layout === StaticMapLayout.MapBottomInfoTop) {
                    // Card on top, shift map center up so point appears down
                    centerLat -= offsetFactor * 8;
                }

                params.set('center', `${centerLat},${centerLng}`);
            }
            // If multiple points and no center specified, let Google auto-center based on markers

            if (isDefined(this.zoom) && !(this.displayPoints.length > 1)) {
                params.set('zoom', String(this.zoom));
            }

            // If multiple points and no zoom specified, let Google auto-fit based on markers

            // Add markers for each point
            for (const [index, point] of this.displayPoints.entries()) {
                const icon = getMarkerIcon(point, new Map<string, CheckIn[]>(), [],false, false);
                let iconUrl  = icon?.url;

                let markerParam: string;
                // Google Static Maps API only accepts HTTP/HTTPS URLs for icons, not data URIs
                if (iconUrl && (iconUrl.startsWith('http://') || iconUrl.startsWith('https://'))) {
                    // Resize icons8 icons to 48x48
                    if (iconUrl.includes('icons8.com')) {
                        iconUrl = iconUrl.replace(/\/\d+\//, '/48/');
                    }
                    // Use custom icon (Google will resize to fit marker size)
                    markerParam = `icon:${iconUrl}|${point.location.latLng.lat},${point.location.latLng.lng}`;
                } else {
                    // Fallback to colored markers with labels (data URIs not supported by Static Maps API)
                    const markerColor = this.getMarkerColor(point);
                    const label = this.displayPoints.length > 1 ? this.getMarkerLabel(index) : '';
                    markerParam = `color:${markerColor}|label:${label}|${point.location.latLng.lat},${point.location.latLng.lng}`;
                }
                params.append('markers', markerParam);
            }
        } else {
            // No points - use topic default center or a default location
            const defaultCenter = this.topic?.default_map_center?.latLng ?? { lat: 0, lng: 0 };
            params.set('center', `${this.centerLat ?? defaultCenter.lat},${this.centerLng ?? defaultCenter.lng}`);
            params.set('zoom', String(this.zoom ?? this.topic?.default_map_zoom ?? 10));
        }

        return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
    }

    /**
     * Get marker color based on point category
     */
    private getMarkerColor(point: GetPointResponse): string {
        const category = this.topic?.categories.find(
            c => c._id?.$oid === point.topic_category_id?.$oid
        );

        // Use featured color if point is featured
        if (point.featured) {
            return this.toStaticMapColor(this.topic?.featured_color, '0xFFD700');
        }

        // Try to extract color from category icon
        if (category?.icon) {
            const match = category.icon.match(/#([0-9A-Fa-f]{6})/);
            if (match) {
                return `0x${match[1]}`;
            }
        }

        // Default to theme color
        return this.toStaticMapColor(this.topic?.header_color, '0x4285F4');
    }

    /**
     * Get style parameters for the Static Maps API based on map style name
     */
    private getStyleParams(styleName: string): string[] {
        // Map style names to basic Static Maps API style parameters
        // Note: Static Maps API styling is more limited than JavaScript API
        switch (styleName) {
            case 'blue':
                return [
                    'feature:water|color:0x193341',
                    'feature:landscape|color:0x2c5a71',
                    'feature:road|element:geometry|color:0x1f3a4c',
                    'feature:poi|visibility:off'
                ];
            case 'muted':
                return [
                    'feature:all|saturation:-50',
                    'feature:poi|visibility:simplified'
                ];
            case 'paper':
                return [
                    'feature:all|saturation:-100|lightness:20',
                    'feature:road|element:geometry|color:0xeeeeee'
                ];
            case 'bright':
                return [
                    'feature:all|saturation:50|lightness:10'
                ];
            case 'sketch':
                return [
                    'feature:all|saturation:-100',
                    'feature:road|element:geometry|color:0xdddddd'
                ];
            default:
                return [];
        }
    }

    /**
     * Get the currently selected point
     */
    private get selectedPoint(): GetPointResponse | undefined {
        return this.displayPoints[this.selectedPointIndex];
    }

    /**
     * Handle point selection change
     */
    private selectPoint(index: number) {
        if (index >= 0 && index < this.displayPoints.length) {
            this.selectedPointIndex = index;
        }
    }

    /**
     * Navigate to previous point
     */
    private previousPoint() {
        if (this.selectedPointIndex > 0) {
            this.selectedPointIndex--;
        } else {
            this.selectedPointIndex = this.displayPoints.length - 1;
        }
    }

    /**
     * Navigate to next point
     */
    private nextPoint() {
        if (this.selectedPointIndex < this.displayPoints.length - 1) {
            this.selectedPointIndex++;
        } else {
            this.selectedPointIndex = 0;
        }
    }

    /**
     * Get category for a point
     */
    private getPointCategory(point: GetPointResponse): TopicCategory | undefined {
        return this.topic?.categories.find(
            c => c._id?.$oid === point.topic_category_id?.$oid
        );
    }

    /**
     * Deep link to the live interactive map, focused on the shown point when there is one.
     * Empty until the topic's share link has loaded, which hides the overlay.
     */
    private get fullMapUrl(): string {
        const base = topicData.publicShareLink?.split('?')[0] ?? '';
        if (!base) {
            return '';
        }
        const pointId = this.selectedPoint?._id.$oid ?? this.pointId;
        return pointId ? `${base}?point_id=${pointId}` : base;
    }

    render() {
        if (this.isLoading) {
            return html`
                <div class="static-map-loading">
                    <loading-element loadingString="Loading map..." ?horizontal=${true} ?small=${true}></loading-element>
                </div>
            `;
        }

        const layoutClass = this.getLayoutClass();
        const showPointInfo = this.layout !== StaticMapLayout.MapOnly && this.displayPoints.length > 0;

        return html`
            <div
                class="static-map-container ${layoutClass}"
                style="width: ${this.mapWidth}px; height: ${this.mapHeight}px;"
            >
                ${this.layout !== StaticMapLayout.CardOnly ? html`
                    <img
                        class="static-map-background"
                        src="${this.getStaticMapUrl()}"
                        alt="Map showing ${this.displayPoints.length} location${this.displayPoints.length !== 1 ? 's' : ''}"
                        loading="lazy"
                    />
                    ${this.fullMapUrl ? html`
                        <a
                            class="open-full-map"
                            href="${this.fullMapUrl}"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open full map"
                        >
                            <mwc-icon class="open-full-map__icon">open_in_new</mwc-icon>
                            <span class="open-full-map__label">Open full map</span>
                        </a>
                    ` : ''}
                ` : ''}
                ${showPointInfo ? this.renderPointInfo() : ''}
            </div>
        `;
    }

    /**
     * Render the point information panel
     */
    private renderPointInfo() {
        const point = this.selectedPoint;
        if (!point) return '';

        const showPagination = this.displayPoints.length > 1;

        return html`
            <div class="point-info-panel">
                ${showPagination ? html`
                    <div class="point-pagination">
                        <button class="pagination-btn" @click="${this.previousPoint}" ?disabled="${this.displayPoints.length <= 1}">
                            <mwc-icon>chevron_left</mwc-icon>
                        </button>
                        <span class="pagination-text">${this.selectedPointIndex + 1} of ${this.displayPoints.length}</span>
                        <button class="pagination-btn" @click="${this.nextPoint}" ?disabled="${this.displayPoints.length <= 1}">
                            <mwc-icon>chevron_right</mwc-icon>
                        </button>
                    </div>
                ` : ''}

                <expanded-point-card
                    .point="${point}"
                    .hideImages=${false}
                    .discoveryView=${false}
                    .listOfPointsView=${false}
                    .customHeight=${`${this.mapHeight - (showPagination ? 60 : 20)}px`}
                    .customImageHeight=${`${this.mapHeight / 3}px`}
                    .hideTopMenu=${true}
                ></expanded-point-card>

                ${showPagination ? html`
                    <div class="point-list">
                        ${this.displayPoints.map((p, i) => html`
                            <button
                                class="point-list-item ${i === this.selectedPointIndex ? 'selected' : ''}"
                                @click="${() => this.selectPoint(i)}"
                            >
                                <span class="point-marker-label">${this.getMarkerLabel(i) || (i + 1)}</span>
                                <span class="point-list-name">${p.name ?? 'Unnamed Point'}</span>
                            </button>
                        `)}
                    </div>
                ` : ''}
            </div>
        `;
    }

    /**
     * Get CSS class for the current layout
     */
    private getLayoutClass(): string {
        switch (this.layout) {
            case StaticMapLayout.MapLeftInfoRight:
                return 'layout-horizontal layout-map-left';
            case StaticMapLayout.MapRightInfoLeft:
                return 'layout-horizontal layout-map-right';
            case StaticMapLayout.MapTopInfoBottom:
                return 'layout-vertical layout-map-top';
            case StaticMapLayout.MapBottomInfoTop:
                return 'layout-vertical layout-map-bottom';
            case StaticMapLayout.MapOnly:
                return 'layout-map-only';
            case StaticMapLayout.CardOnly:
                return 'layout-card-only';
            default:
                return 'layout-horizontal layout-map-left';
        }
    }

    static get styles() {
        return css`
            :host {
                display: block;
                font-family: var(--main-font, Arial, sans-serif);
            }

            .static-map-loading {
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 200px;
                background: var(--theme-light-scrollbar-color, #f5f5f5);
                border-radius: 8px;
            }

            .static-map-container {
                position: relative;
                display: block;
                /* The inline width/height carry the embed's requested size; cap to the
                   frame so a wide embed on a narrow screen can't overflow off-screen. */
                max-width: 100%;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1);
                margin: 0 auto;
            }

            .open-full-map {
                position: absolute;
                top: 12px;
                right: 12px;
                z-index: 2;
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 6px 12px;
                background: rgba(255, 255, 255, 0.95);
                color: var(--main-primary-color, #1f1b2d);
                border-radius: 20px;
                font-size: 13px;
                font-weight: 600;
                text-decoration: none;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
                transition: background 0.15s ease, transform 0.15s ease;
            }

            .open-full-map:hover {
                background: #fff;
                transform: translateY(-1px);
            }

            .open-full-map__icon {
                --mdc-icon-size: 16px;
                font-size: 16px;
            }

            /* Pin the button to the map side, opposite the card. */
            .static-map-container.layout-map-left .open-full-map {
                left: 12px;
                right: auto;
            }

            .static-map-container.layout-map-bottom .open-full-map {
                top: auto;
                bottom: 12px;
            }

            /* On small screens collapse to a compact round icon button. */
            @media only screen and (max-width: 550px) {
                .open-full-map {
                    top: 8px;
                    right: 8px;
                    padding: 9px;
                    gap: 0;
                    border-radius: 50%;
                }

                .static-map-container.layout-map-left .open-full-map {
                    left: 8px;
                }

                .static-map-container.layout-map-bottom .open-full-map {
                    bottom: 8px;
                }

                .open-full-map__label {
                    display: none;
                }

                /* Give the card room to breathe on phones instead of a 50% column. */
                .static-map-container.layout-map-left .point-info-panel,
                .static-map-container.layout-map-right .point-info-panel {
                    max-width: 85%;
                }
            }

            :host {
                display: block;
                width: 100%;
            }

            :host([mapWidth]) .static-map-container {
                width: var(--map-width, 100%);
                height: var(--map-height, 500px);
            }

            /* Layout alignments */
            .layout-horizontal {
                align-items: center;
                justify-content: center;
            }

            .layout-vertical {
                align-items: center;
                justify-content: center;
                flex-direction: column;
            }

            .layout-map-only .point-info-panel {
                display: none;
            }

            .static-map-background {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
                z-index: 0;
            }

            .point-info-panel {
                position: absolute;
                z-index: 1;
                width: 400px;
                max-height: 90%;
                max-width: 90%;
                padding: 5px;
                display: flex;
                overflow: auto;
                flex-direction: column;
                gap: 16px;
                /* Default position - right side */
                right: 24px;
                top: 50%;
                transform: translateY(-50%);
            }

            /* Position card based on layout */
            .static-map-container.layout-map-left .point-info-panel {
                right: 24px !important;
                left: auto !important;
                top: 50% !important;
                bottom: auto !important;
                max-width: 50%;
                transform: translateY(-50%) !important;
            }

            .static-map-container.layout-map-right .point-info-panel {
                left: 24px !important;
                right: auto !important;
                top: 50% !important;
                bottom: auto !important;
                max-width: 50%;
                transform: translateY(-50%) !important;
            }

            .static-map-container.layout-map-top .point-info-panel {
                bottom: 24px !important;
                top: auto !important;
                left: 50% !important;
                right: auto !important;
                transform: translateX(-50%) !important;
                width: 600px;
                max-height: 300px;
            }

            .static-map-container.layout-map-bottom .point-info-panel {
                top: 24px !important;
                bottom: auto !important;
                left: 50% !important;
                right: auto !important;
                transform: translateX(-50%) !important;
                width: 600px;
                max-height: 300px;
            }

            /* Layout variations for overlay style */
            .layout-map-only .point-info-panel {
                display: none;
            }

            .layout-card-only .static-map-background {
                display: none !important;
            }

            .static-map-container.layout-card-only {
                background: transparent;
            }

            .layout-card-only .point-info-panel {
                position: relative;
                margin: 0;
                width: 100%;
                max-width: 100%;
                height: 100%;
                max-height: 100%;
                transform: none !important;
                top: auto !important;
                left: auto !important;
                right: auto !important;
                bottom: auto !important;
            }

            .point-pagination {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding-bottom: 12px;
                border-bottom: 1px solid var(--theme-border-color, #e0e0e0);
            }

            .pagination-btn {
                background: none;
                border: 1px solid var(--theme-border-color, #e0e0e0);
                border-radius: 4px;
                padding: 4px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: background-color 0.2s;
            }

            .pagination-btn:hover:not(:disabled) {
                background: var(--theme-light-scrollbar-color, #f5f5f5);
            }

            .pagination-btn:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .pagination-btn mwc-icon {
                --mdc-icon-size: 20px;
            }

            .pagination-text {
                font-size: 14px;
                color: var(--theme-input-color, #333);
            }

            .point-card-wrapper {
                flex: 1;
                overflow-y: auto;
                margin: -8px 0;
            }

            .point-card-wrapper expanded-point-card {
                display: block;
                width: 100%;
                margin: 8px 0;
            }

            .point-info-content {
                flex: 1;
            }

            .point-category {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 12px;
                color: var(--theme-lighter-text-color, #666);
                margin-bottom: 8px;
            }

            .point-category img {
                border-radius: 50%;
            }

            .point-title {
                font-size: 18px;
                font-weight: 700;
                color: var(--main-primary-color, #102C3D);
                margin: 0 0 8px 0;
                display: flex;
                align-items: center;
                gap: 4px;
            }

            .featured-icon {
                color: var(--main-featured-color, #FFD700);
                --mdc-icon-size: 20px;
            }

            .point-address {
                display: flex;
                align-items: flex-start;
                gap: 4px;
                font-size: 13px;
                color: var(--theme-input-color, #333);
                margin-bottom: 8px;
            }

            .address-icon {
                --mdc-icon-size: 16px;
                flex-shrink: 0;
                color: var(--theme-lighter-text-color, #666);
            }

            .point-description {
                font-size: 14px;
                color: var(--theme-input-color, #333);
                line-height: 1.5;
                margin-bottom: 8px;
            }

            .point-special-offer {
                font-size: 13px;
                padding: 8px;
                border: 1px dashed var(--theme-input-color, #333);
                border-radius: 4px;
            }

            .point-list {
                display: flex;
                flex-direction: column;
                gap: 4px;
                margin-top: 12px;
                padding-top: 12px;
                border-top: 1px solid var(--theme-border-color, #e0e0e0);
                max-height: 150px;
                overflow-y: auto;
            }

            .point-list-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px;
                border: 1px solid var(--theme-border-color, #e0e0e0);
                border-radius: 4px;
                background: var(--theme-white-color, #fff);
                cursor: pointer;
                transition: background-color 0.2s, border-color 0.2s;
                text-align: left;
            }

            .point-list-item:hover {
                background: var(--theme-light-scrollbar-color, #f5f5f5);
            }

            .point-list-item.selected {
                border-color: var(--main-primary-color, #102C3D);
                background: var(--theme-light-scrollbar-color, #f5f5f5);
            }

            .point-marker-label {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background: var(--main-primary-color, #102C3D);
                color: white;
                font-size: 12px;
                font-weight: bold;
                flex-shrink: 0;
            }

            .point-list-name {
                font-size: 13px;
                color: var(--theme-input-color, #333);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            /* Responsive styles */
            @media only screen and (max-width: 768px) {
                .static-map-container {
                    min-height: 400px;
                    border-radius: 8px;
                }

                .point-info-panel {
                    width: 95%;
                    max-height: 85%;
                }
            }

            @media only screen and (max-width: 480px) {
                .static-map-container {
                    min-height: 350px;
                }

                .point-info-panel {
                    gap: 12px;
                }

                .point-title {
                    font-size: 16px;
                }
            }
        `;
    }
}
